import { Link } from "react-router-dom";
import { ShieldCheck, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import {
  ValuePillars, ServicesGrid, CustomerJourneys, HowItWorks, CtaBand,
  ConsultButton, WhatsAppButton, FaqAccordion, SectionHeading,
} from "@/components/sections";
import { site } from "@/config/site";

const HERO_IMG =
  "https://images.unsplash.com/photo-1758518730083-4c12527b6742?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBhY2NvdW50YW50JTIwd29ya2luZyUyMG9mZmljZSUyMG1lZXRpbmclMjBidXNpbmVzcyUyMGNvbnN1bHRhbnR8ZW58MHx8fHwxNzkxMjY1NDAzfDA&ixlib=rb-4.1.0&q=85";

const FAQS = [
  { q: "What services does Bonsai Financial provide?", a: "Bonsai Financial provides bookkeeping, accounting and financial reporting, tax services, payroll, and broader business and financial support — tailored to the needs of each client." },
  { q: "Who does Bonsai Financial work with?", a: "We work with individuals, professionals, entrepreneurs, independent contractors, consultants, professional practices and small to medium businesses across South Africa." },
  { q: "Can Bonsai assist clients across South Africa?", a: "Yes. Bonsai Financial works with clients in various provinces and can support you remotely where appropriate." },
  { q: "How does the consultation process work?", a: "You complete a short assessment so we understand your situation. Your information is reviewed and a consultation can be arranged to discuss the right support for you." },
  { q: "How are fees determined?", a: "Solutions are tailored according to the services, requirements and complexity of each client. Fees are discussed during consultation." },
];

export default function Home() {
  return (
    <>
      <Seo
        title="Accountant & Tax Services South Africa"
        description="Bonsai Financial provides practical accounting, tax, bookkeeping and business support for South African professionals, entrepreneurs and growing businesses. Led by a SAIPA-registered Professional Accountant (SA)."
        path="/"
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#F9F8F5]">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-14 sm:px-6 sm:py-20 lg:grid-cols-2 lg:px-8 lg:py-28">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full border border-[#2C5E43]/20 bg-white px-3 py-1.5 text-xs font-semibold text-[#2C5E43]">
              <ShieldCheck className="h-4 w-4 text-[#C68B59]" />
              {site.credentials.trustLine}
            </span>

            <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-[#18201B] sm:text-5xl lg:text-6xl">
              Accounting. Tax. Bookkeeping.{" "}
              <span className="text-[#2C5E43]">Business Support.</span>
            </h1>

            <p className="mt-5 max-w-xl text-lg leading-relaxed text-[#526057]">
              Professional accounting and tax services for South African professionals,
              entrepreneurs and growing businesses. Running a business is demanding enough
              without worrying about bookkeeping, tax deadlines and financial administration.
            </p>

            <p className="mt-3 max-w-xl text-base leading-relaxed text-[#526057]">
              Bonsai Financial provides practical support to help you stay organised, compliant
              and informed.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <ConsultButton from="hero" testId="hero-consult-btn" className="h-12 px-6 text-base" />
              <WhatsAppButton context="general" testId="hero-whatsapp-btn" className="h-12 px-6 text-base" />
            </div>
          </div>

          <div className="relative animate-fade-up">
            <div className="absolute -inset-4 rounded-[2rem] bg-[#133827]/5 blur-xl" />
            <div className="relative overflow-hidden rounded-[1.75rem] border border-slate-200 shadow-2xl shadow-[#133827]/10">
              <img
                src={HERO_IMG}
                alt="Professional accountant meeting with a South African business owner"
                loading="eager"
                className="aspect-[4/3] w-full object-cover"
              />
              <div className="absolute bottom-4 left-4 right-4 rounded-xl bg-white/95 p-4 backdrop-blur">
                <p className="text-xs font-semibold uppercase tracking-wider text-[#2C5E43]">Led by {site.founderName}</p>
                <p className="mt-0.5 text-sm text-[#526057]">{site.credentials.designation} · {site.credentials.taxTechnician}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ValuePillars />
      <CustomerJourneys />
      <ServicesGrid />
      <HowItWorks />

      {/* FAQ preview */}
      <section className="bg-white py-16 sm:py-24">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:px-8">
          <SectionHeading eyebrow="Questions" title="Frequently asked questions." intro="A few things prospective clients often want to know before getting in touch." />
          <div>
            <FaqAccordion items={FAQS} />
            <Link to="/contact" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#2C5E43] hover:text-[#133827]" data-testid="home-more-faq-link">
              Have another question? Contact us <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <CtaBand from="home-cta" />
    </>
  );
}
